import { motion } from 'framer-motion';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import BackToTop from '../../components/BackToTop';

const TermsOfService = () => {
  const effectiveDate = 'January 1, 2026';

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-cream">
      <Header />
      <main className="flex-1 pt-20 sm:pt-24 md:pt-28">
        {/* Hero Section */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 md:px-8 py-12 sm:py-16">
          <motion.h1
            className="font-display text-3xl sm:text-4xl md:text-5xl font-semibold text-navy mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Terms of Service
          </motion.h1>
          <motion.p
            className="font-body text-sm sm:text-base text-slate"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Effective Date: {effectiveDate}
          </motion.p>
        </section>

        {/* Content */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 md:px-8 pb-16 sm:pb-20">
          <motion.div
            className="prose prose-lg max-w-none"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: { staggerChildren: 0.1 }
              }
            }}
          >
            {/* Introduction */}
            <motion.div variants={fadeInUp} className="mb-10">
              <p className="font-body text-base sm:text-lg text-charcoal leading-relaxed">
                Welcome to The Poetic Method™. These Terms of Service ("Terms") govern your use of our website
                and services. By accessing our website or engaging our services, you agree to be bound by these
                Terms. If you do not agree to these Terms, please do not use our website or services.
              </p>
            </motion.div>

            {/* Services Overview */}
            <motion.div variants={fadeInUp} className="mb-10">
              <h2 className="font-display text-xl sm:text-2xl text-navy mb-4">Services Overview</h2>
              <p className="font-body text-base text-charcoal leading-relaxed mb-4">
                The Poetic Method™ provides organizational consulting services for healthcare organizations,
                including but not limited to:
              </p>
              <ul className="list-disc list-inside space-y-2 font-body text-base text-charcoal">
                <li>Culture Catalyst Workshops</li>
                <li>Leadership Accelerator Programs</li>
                <li>Operational Excellence Projects</li>
                <li>Culture Assessments</li>
                <li>Transformation Partnerships</li>
                <li>Speaking Engagements</li>
              </ul>
              <p className="font-body text-base text-charcoal leading-relaxed mt-4">
                Specific service details, deliverables, timelines, and fees are outlined in separate engagement
                agreements between The Poetic Method™ and clients.
              </p>
            </motion.div>

            {/* Client Responsibilities */}
            <motion.div variants={fadeInUp} className="mb-10">
              <h2 className="font-display text-xl sm:text-2xl text-navy mb-4">Client Responsibilities</h2>
              <p className="font-body text-base text-charcoal leading-relaxed mb-4">
                When engaging our services, clients agree to:
              </p>
              <ul className="list-disc list-inside space-y-2 font-body text-base text-charcoal">
                <li>Provide accurate and complete information necessary for service delivery</li>
                <li>Designate appropriate personnel to participate in engagements</li>
                <li>Make timely decisions and provide feedback as requested</li>
                <li>Comply with payment terms outlined in engagement agreements</li>
                <li>Respect confidentiality of proprietary methodologies and materials</li>
                <li>Communicate openly about organizational needs and challenges</li>
              </ul>
            </motion.div>

            {/* Intellectual Property */}
            <motion.div variants={fadeInUp} className="mb-10">
              <h2 className="font-display text-xl sm:text-2xl text-navy mb-4">Intellectual Property</h2>
              <p className="font-body text-base text-charcoal leading-relaxed mb-4">
                The Poetic Method™ and all associated trademarks, logos, methodologies, frameworks, training
                materials, and content are the exclusive property of The Poetic Method™ and are protected by
                intellectual property laws.
              </p>
              <p className="font-body text-base text-charcoal leading-relaxed mb-4">
                Clients may not:
              </p>
              <ul className="list-disc list-inside space-y-2 font-body text-base text-charcoal">
                <li>Reproduce, distribute, or publicly display our proprietary materials without written consent</li>
                <li>Modify or create derivative works based on our methodologies</li>
                <li>Use our trademarks or branding without prior authorization</li>
                <li>Reverse engineer or attempt to extract our proprietary frameworks</li>
              </ul>
              <p className="font-body text-base text-charcoal leading-relaxed mt-4">
                Work products created specifically for a client during an engagement may be subject to separate
                intellectual property arrangements as specified in the engagement agreement.
              </p>
            </motion.div>

            {/* Confidentiality */}
            <motion.div variants={fadeInUp} className="mb-10">
              <h2 className="font-display text-xl sm:text-2xl text-navy mb-4">Confidentiality</h2>
              <p className="font-body text-base text-charcoal leading-relaxed">
                The Poetic Method™ maintains strict confidentiality regarding all client information, organizational
                data, and engagement details. We will not disclose confidential information to third parties without
                client consent, except as required by law. Clients are similarly expected to maintain confidentiality
                regarding our proprietary methodologies and materials.
              </p>
            </motion.div>

            {/* Payment Terms */}
            <motion.div variants={fadeInUp} className="mb-10">
              <h2 className="font-display text-xl sm:text-2xl text-navy mb-4">Payment Terms</h2>
              <p className="font-body text-base text-charcoal leading-relaxed mb-4">
                Payment terms are specified in individual engagement agreements. General terms include:
              </p>
              <ul className="list-disc list-inside space-y-2 font-body text-base text-charcoal">
                <li>Deposits may be required to secure engagement dates</li>
                <li>Invoices are due within 30 days unless otherwise specified</li>
                <li>Late payments may be subject to interest charges</li>
                <li>Cancellation policies vary by service type and are outlined in engagement agreements</li>
              </ul>
            </motion.div>

            {/* Limitation of Liability */}
            <motion.div variants={fadeInUp} className="mb-10">
              <h2 className="font-display text-xl sm:text-2xl text-navy mb-4">Limitation of Liability</h2>
              <p className="font-body text-base text-charcoal leading-relaxed mb-4">
                To the fullest extent permitted by law:
              </p>
              <ul className="list-disc list-inside space-y-2 font-body text-base text-charcoal">
                <li>
                  The Poetic Method™ provides services on an "as is" basis and makes no warranties, express or
                  implied, regarding service outcomes
                </li>
                <li>
                  We shall not be liable for any indirect, incidental, special, consequential, or punitive damages
                </li>
                <li>
                  Our total liability shall not exceed the fees paid by the client for the specific services giving
                  rise to the claim
                </li>
                <li>
                  Clients acknowledge that organizational transformation involves multiple factors beyond our control
                </li>
              </ul>
            </motion.div>

            {/* Indemnification */}
            <motion.div variants={fadeInUp} className="mb-10">
              <h2 className="font-display text-xl sm:text-2xl text-navy mb-4">Indemnification</h2>
              <p className="font-body text-base text-charcoal leading-relaxed">
                Clients agree to indemnify, defend, and hold harmless The Poetic Method™, its owner, employees,
                and agents from any claims, damages, losses, or expenses arising from the client's breach of
                these Terms, misuse of services, or violation of any third-party rights.
              </p>
            </motion.div>

            {/* Termination */}
            <motion.div variants={fadeInUp} className="mb-10">
              <h2 className="font-display text-xl sm:text-2xl text-navy mb-4">Termination</h2>
              <p className="font-body text-base text-charcoal leading-relaxed">
                Either party may terminate an engagement in accordance with the terms specified in the engagement
                agreement. The Poetic Method™ reserves the right to terminate services if a client breaches these
                Terms or engagement agreement terms. Upon termination, clients remain responsible for payment
                of services rendered and any applicable cancellation fees.
              </p>
            </motion.div>

            {/* Governing Law */}
            <motion.div variants={fadeInUp} className="mb-10">
              <h2 className="font-display text-xl sm:text-2xl text-navy mb-4">Governing Law</h2>
              <p className="font-body text-base text-charcoal leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of the State of Florida,
                without regard to its conflict of law provisions. Any disputes arising under these Terms shall be
                resolved in the state or federal courts located in Florida.
              </p>
            </motion.div>

            {/* Changes to Terms */}
            <motion.div variants={fadeInUp} className="mb-10">
              <h2 className="font-display text-xl sm:text-2xl text-navy mb-4">Changes to These Terms</h2>
              <p className="font-body text-base text-charcoal leading-relaxed">
                We reserve the right to modify these Terms at any time. Changes will be effective upon posting
                to our website with an updated "Effective Date." Your continued use of our website or services
                after changes are posted constitutes acceptance of the modified Terms.
              </p>
            </motion.div>

            {/* Severability */}
            <motion.div variants={fadeInUp} className="mb-10">
              <h2 className="font-display text-xl sm:text-2xl text-navy mb-4">Severability</h2>
              <p className="font-body text-base text-charcoal leading-relaxed">
                If any provision of these Terms is found to be unenforceable or invalid, that provision shall be
                limited or eliminated to the minimum extent necessary, and the remaining provisions shall continue
                in full force and effect.
              </p>
            </motion.div>

            {/* Contact */}
            <motion.div variants={fadeInUp} className="mb-10">
              <h2 className="font-display text-xl sm:text-2xl text-navy mb-4">Contact Information</h2>
              <p className="font-body text-base text-charcoal leading-relaxed mb-4">
                For questions about these Terms of Service, please contact us:
              </p>
              <div className="bg-white p-6 rounded-lg border border-mist">
                <p className="font-body text-base text-charcoal">
                  <strong className="text-navy">The Poetic Method™</strong>
                </p>
                <p className="font-body text-base text-charcoal mt-2">
                  Email:{' '}
                  <a
                    href="mailto:poeticmethod101@gmail.com"
                    className="text-teal hover:underline transition-colors"
                  >
                    poeticmethod101@gmail.com
                  </a>
                </p>
                <p className="font-body text-base text-slate mt-1">
                  South Florida · Serving organizations nationwide
                </p>
              </div>
            </motion.div>
          </motion.div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
};

export default TermsOfService;
